import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ApiService } from '../api';
import Icon from './Icon';
import ActionSheet from './ActionSheet';
import { useToast } from './Toast';

interface Backup {
  filename: string;
  size: number;
  created_at: string;
}

type ConfirmState =
  | { kind: 'restore'; backup: Backup }
  | { kind: 'delete'; backup: Backup }
  | null;

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const formatDate = (s: string): string =>
  new Date(s).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

const BackupManager: React.FC = () => {
  const [backups, setBackups] = useState<Backup[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [confirm, setConfirm] = useState<ConfirmState>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await ApiService.listBackups();
      setBackups(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      toast.error('Backups konnten nicht geladen werden');
      setBackups([]);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async () => {
    try {
      setLoading(true);
      const res = await ApiService.createBackup();
      toast.success(`Backup erstellt: ${res.filename}`);
      await load();
    } catch (e) {
      console.error(e);
      toast.error('Backup konnte nicht erstellt werden');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (filename: string) => {
    try {
      const blob = await ApiService.downloadBackup(filename);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (e) {
      console.error(e);
      toast.error('Download fehlgeschlagen');
    }
  };

  const handleRestore = async () => {
    if (confirm?.kind !== 'restore') return;
    try {
      setLoading(true);
      await ApiService.restoreBackup(confirm.backup.filename);
      toast.success('Backup wiederhergestellt');
      await load();
    } catch (e) {
      console.error(e);
      toast.error('Wiederherstellen fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (confirm?.kind !== 'delete') return;
    try {
      setLoading(true);
      await ApiService.deleteBackup(confirm.backup.filename);
      toast.success('Backup gelöscht');
      await load();
    } catch (e) {
      console.error(e);
      toast.error('Löschen fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.db')) {
      toast.error('Nur .db Dateien erlaubt');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    if (file.size > 100 * 1024 * 1024) {
      toast.error('Datei zu groß (max 100 MB)');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    const rename = () => {
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const base = file.name.replace('.db', '');
      return new File([file], `${base}_${timestamp}.db`, { type: file.type });
    };

    setUploading(true);
    try {
      await ApiService.uploadBackup(file);
      toast.success('Backup hochgeladen');
      await load();
    } catch (err: any) {
      if (err.response?.status === 409) {
        try {
          await ApiService.uploadBackup(rename());
          toast.success('Backup hochgeladen (umbenannt)');
          await load();
        } catch (e2) {
          console.error(e2);
          toast.error('Upload fehlgeschlagen');
        }
      } else if (err.response?.status === 400) {
        toast.error('Ungültige Datei');
      } else {
        toast.error('Upload fehlgeschlagen');
      }
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <>
      <div className="cta-card">
        <div className="cta-title">Deine Daten, sicher.</div>
        <div className="cta-sub">Lade dein aktuelles Spiel hoch oder stelle ein Backup wieder her.</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-glass" onClick={handleCreate} disabled={loading || uploading}>
            <Icon name="archive" size={18} />
            {loading ? 'Erstelle…' : 'Backup erstellen'}
          </button>
          <button
            className="btn btn-glass"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            <Icon name="upload" size={18} />
            {uploading ? 'Lade hoch…' : 'Hochladen'}
          </button>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".db"
          style={{ display: 'none' }}
          onChange={handleUpload}
        />
      </div>

      <div className="section-header">
        <span>Verfügbare Backups ({backups.length})</span>
      </div>

      {backups.length === 0 ? (
        <div className="empty-ios">
          <div className="empty-emoji">📦</div>
          <div className="empty-title">Noch keine Backups</div>
          <div className="footnote">Erstelle oben dein erstes Backup.</div>
        </div>
      ) : (
        <div className="ios-list">
          {backups.map((b) => (
            <div key={b.filename} className="ios-row">
              <div className="row-icon purple"><Icon name="archive" size={16} /></div>
              <div className="row-main">
                <div className="row-title mono" style={{ fontSize: 14 }}>{b.filename}</div>
                <div className="row-subtitle">
                  {formatFileSize(b.size)} · {formatDate(b.created_at)}
                </div>
              </div>
              <div className="row-aside">
                <button className="btn btn-sm btn-tinted btn-icon" onClick={() => handleDownload(b.filename)} aria-label="Download">
                  <Icon name="download" size={18} />
                </button>
                <button className="btn btn-sm btn-gray btn-icon" onClick={() => setConfirm({ kind: 'restore', backup: b })} aria-label="Wiederherstellen">
                  <Icon name="refresh" size={18} />
                </button>
                <button className="btn btn-sm btn-destructive-tinted btn-icon" onClick={() => setConfirm({ kind: 'delete', backup: b })} aria-label="Löschen">
                  <Icon name="trash" size={18} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="section-footer">Backups werden als SQLite-Datenbank gespeichert.</div>

      <ActionSheet
        open={confirm?.kind === 'restore'}
        onClose={() => setConfirm(null)}
        title="Backup wiederherstellen?"
        message={confirm?.kind === 'restore' ? `„${confirm.backup.filename}“ überschreibt die aktuelle Datenbank. Ein Pre-Restore-Backup wird automatisch angelegt.` : undefined}
        actions={[
          { label: 'Wiederherstellen', style: 'default', onClick: handleRestore },
          { label: 'Abbrechen', style: 'cancel' },
        ]}
      />

      <ActionSheet
        open={confirm?.kind === 'delete'}
        onClose={() => setConfirm(null)}
        title="Backup löschen?"
        message={confirm?.kind === 'delete' ? `„${confirm.backup.filename}“ wird dauerhaft entfernt.` : undefined}
        actions={[
          { label: 'Löschen', style: 'destructive', onClick: handleDelete },
          { label: 'Abbrechen', style: 'cancel' },
        ]}
      />
    </>
  );
};

export default BackupManager;
